'use client';

import { ComponentProps, FC, ReactElement, ReactNode } from 'react';

import { Product, ProductPlaceholder, Slider } from '@/components';
import { GProduct } from '@/types';

import styles from './reorder.module.scss';

const REORDER_BREAKPOINTS = {
  0: { slidesPerView: 2 },
  1024: { slidesPerView: 3 },
  2048: { slidesPerView: 4 },
};

interface IReorderCarouselProps {
  isLoading: boolean;
  products: GProduct[];
  isLargeCard?: boolean;
  spaceBetween: ComponentProps<typeof Slider>['spaceBetween'];
  breakpoints?: ComponentProps<typeof Slider>['breakpoints'];
  slidesPerView?: ComponentProps<typeof Slider>['slidesPerView'];
  showSliderHeader?: boolean;
  showSlidesOutsideContainer?: boolean;
  activatedHover?: boolean;
  isNotPaddingMobile?: boolean;
  skeletonCount?: number;
  sliderHeading?: ReactElement;
  seeAllElement?: ReactNode;
  analyticsListName?: string;
}

const ReorderCarousel: FC<IReorderCarouselProps> = ({
  isLoading,
  products,
  isLargeCard,
  spaceBetween,
  breakpoints,
  slidesPerView,
  showSliderHeader = false,
  showSlidesOutsideContainer = true,
  activatedHover = false,
  isNotPaddingMobile,
  skeletonCount = 4,
  sliderHeading,
  seeAllElement,
  analyticsListName = 'Reorder',
}) => (
  <Slider
    activatedHover={activatedHover}
    ariaLabel="Reorder products carousel"
    breakpoints={breakpoints ?? (isLargeCard ? undefined : REORDER_BREAKPOINTS)}
    centeredSlides={false}
    className={styles.reorder__carousel}
    freeMode
    isNotPaddingMobile={isNotPaddingMobile}
    isSliderProductCard
    key={isLoading ? 'loading' : `loaded-${products.length}`}
    seeAllElement={seeAllElement}
    showSliderHeader={showSliderHeader}
    showSlidesOutsideContainer={showSlidesOutsideContainer}
    sliderHeading={sliderHeading}
    slidesPerView={slidesPerView}
    spaceBetween={spaceBetween}
  >
    {isLoading
      ? new Array(skeletonCount)
          .fill(null)
          .map((_, index) => (
            <ProductPlaceholder key={index} largeCard={isLargeCard} />
          ))
      : products?.map(product => (
          <Product
            analyticsListName={analyticsListName}
            className={styles.reorder__product}
            key={product.id}
            product={product}
          />
        ))}
  </Slider>
);

export { ReorderCarousel };
